/**
 * GlobalAccountingPanel — v49.6. Unified admin revenue view aggregating
 * every DS-side income source across the app (Ads, e-Dépôts, MyMouv,
 * MyShop, AP-IA, MyJob, RDV, promote profile, eSIM markup…).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wallet, Download, Loader2, TrendingUp } from 'lucide-react';

const SRC_LABEL = {
  ads: 'Publicités',
  edepot: 'e-Dépôts',
  mymouv_commission: 'MyMouv (commission)',
  myshop_commission: 'MyShop (commission)',
  apia: 'AP-IA',
  promote_profile: 'Promotion profil',
  rdv_pack: 'RDV — packs',
  myjob_pack: 'MyJob — packs',
  esim_commission: 'eSIM (markup)',
};

const SRC_COLOR = {
  ads: 'bg-violet-500', edepot: 'bg-fuchsia-500',
  mymouv_commission: 'bg-cyan-500', myshop_commission: 'bg-amber-500',
  apia: 'bg-blue-500', promote_profile: 'bg-pink-500',
  rdv_pack: 'bg-emerald-500', myjob_pack: 'bg-orange-500',
  esim_commission: 'bg-rose-500',
};

const money = (v, cur = 'EUR') => {
  try { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: cur }).format(v || 0); }
  catch (_) { return `${(v || 0).toFixed(2)} ${cur}`; }
};

const GlobalAccountingPanel = () => {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get('/api/admin/global-accounting/summary', { params: { period } });
        if (alive) setData(r.data);
      } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
      finally { setLoading(false); }
    })();
    return () => { alive = false; };
  }, [period]);

  const exportCsv = async () => {
    try {
      const r = await axios.get('/api/admin/global-accounting/export.csv', { params: { period }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `destine-global-${period}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (_) { toast.error('Export impossible'); }
  };

  return (
    <section className="p-4 rounded-3xl bg-gradient-to-br from-emerald-500/10 to-violet-500/10 border border-white/10 space-y-3" data-testid="global-accounting-panel">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-300" /> Comptabilité globale Destine</h3>
          <p className="text-xs text-white/60">Tous les revenus DS agrégés (Ads, e-Dépôts, MyMouv, MyShop, AP-IA, MyJob, RDV, Promotion, eSIM…)</p>
          {data?.esim_markup_percent != null && (
            <p className="text-[11px] text-white/40 mt-1">Markup eSIM Airalo : <strong>{data.esim_markup_percent.toFixed(0)}%</strong></p>
          )}
        </div>
        <div className="flex gap-2">
          <select value={period} onChange={e => setPeriod(e.target.value)} className="bg-white/10 border border-white/15 rounded-lg px-2 py-1.5 text-xs" data-testid="global-acc-period">
            <option value="day" className="bg-gray-900">Jour</option>
            <option value="week" className="bg-gray-900">Semaine</option>
            <option value="month" className="bg-gray-900">Mois</option>
            <option value="year" className="bg-gray-900">Année</option>
            <option value="all" className="bg-gray-900">Tout</option>
          </select>
          <button onClick={exportCsv} className="px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs flex items-center gap-1" data-testid="global-acc-export"><Download className="w-3.5 h-3.5" /> CSV</button>
        </div>
      </div>

      {loading || !data ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-300" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <Kpi title="Total revenus (EUR)" value={money(data.totals?.revenue_eur_total)} color="emerald" />
            <Kpi title="Frais Stripe estimés" value={money(data.totals?.estimated_stripe_fees_eur)} color="orange" />
            <Kpi title="Net après Stripe" value={money(data.totals?.net_eur_after_stripe)} color="cyan" />
          </div>

          <div>
            <h4 className="text-sm font-bold mb-2 flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-emerald-300" /> Par source (EUR)</h4>
            <div className="space-y-1">
              {(data.totals?.by_source_eur || []).map(s => (
                <div key={s.source} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/5 text-sm" data-testid={`global-acc-src-${s.source}`}>
                  <span className="text-white/80 flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${SRC_COLOR[s.source] || 'bg-white/40'}`} />{SRC_LABEL[s.source] || s.source}</span>
                  <span className="font-semibold text-emerald-300">{money(s.revenue_eur)}</span>
                </div>
              ))}
              {(data.totals?.by_source_eur || []).length === 0 && <p className="text-xs text-white/40 italic">Aucun revenu sur la période.</p>}
            </div>
          </div>

          {/* v49.7 — Monthly stacked evolution chart (EUR) */}
          {(data.totals?.by_month_stacked || []).length > 0 && (
            <div data-testid="global-acc-monthly-chart">
              <h4 className="text-sm font-bold mb-2">Évolution mensuelle (EUR)</h4>
              {(() => {
                const rows = data.totals.by_month_stacked;
                const maxMonth = rows.reduce((m, r) => Math.max(m, r.total_eur || 0), 1) || 1;
                return (
                  <div className="space-y-1.5">
                    {rows.map(row => (
                      <div key={row.month} className="grid grid-cols-[70px_1fr_80px] items-center gap-2 text-xs">
                        <span className="text-white/60 font-mono">{row.month}</span>
                        <div className="h-4 rounded-full bg-white/5 overflow-hidden flex" title={`${row.month} — ${money(row.total_eur)}`}>
                          {(row.sources || []).map(s => (
                            s.amount_eur > 0 ? (
                              <div key={s.source} className={`h-full ${SRC_COLOR[s.source] || 'bg-white/40'}`}
                                   style={{ width: `${(s.amount_eur / maxMonth) * 100}%` }}
                                   title={`${SRC_LABEL[s.source] || s.source}: ${money(s.amount_eur)}`} />
                            ) : null
                          ))}
                        </div>
                        <span className="text-right font-semibold text-emerald-200">{money(row.total_eur)}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-white/60">
                {Object.entries(SRC_LABEL).map(([k, label]) => (
                  <span key={k} className="inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${SRC_COLOR[k]}`} />{label}</span>
                ))}
              </div>
            </div>
          )}

          <details className="mt-2">
            <summary className="text-xs text-white/60 cursor-pointer">Détail par transaction ({data.rows?.length || 0})</summary>
            <div className="mt-2 max-h-80 overflow-y-auto space-y-1">
              {(data.rows || []).map((r, i) => (
                <div key={i} className="flex items-center justify-between px-2 py-1 rounded bg-white/5 text-xs">
                  <span className="text-white/70">{r.ts?.slice(0, 10)} · {SRC_LABEL[r.source] || r.source}</span>
                  <span className="font-mono font-semibold">{money(r.amount_eur, r.currency)}</span>
                </div>
              ))}
            </div>
          </details>
        </>
      )}
    </section>
  );
};

const Kpi = ({ title, value, color }) => (
  <div className={`p-3 rounded-xl bg-${color}-500/10 border border-${color}-500/30`}>
    <p className="text-[10px] text-white/60">{title}</p>
    <p className={`text-lg font-bold text-${color}-200`}>{value}</p>
  </div>
);

export default GlobalAccountingPanel;
